import {
  convertSampleCollectionDate,
  getEstimatedResultsDeliveryDate,
  isConnectPortal,
  isOncologyPortal,
} from "@app/utils";
import { IntlShape } from "react-intl";
import { messages } from "../messages";
import {
  ManagedBy,
  SampleType,
  TestCard,
  TestCardStatus,
  TestStatus,
  TestType,
} from "../types";
import { SimpleOrderWorkflowState } from "@app/provider/order";
import { isHorizonOrPanorama } from "@app/utils/orderHelpers";
import {
  getProsperaEbdd,
  hasProsperaEbddPassed,
} from "@app/utils/prosperaEbdd";
import { Utils } from "@natera/material/lib/date";

export const getDescription = (
  intl: IntlShape,
  testCard: TestCard
): string | undefined => {
  // Rules-based cards: use provided i18nKey with pre-computed params from server
  if (testCard.i18nKeys?.description) {
    // Use server-provided params (date, sampleCollectionDate) - no frontend calculation needed
    const params = testCard.i18nKeys.params ?? {};
    return intl.formatMessage({ id: testCard.i18nKeys.description }, params);
  }

  // Legacy: Compute based on status
  switch (testCard.status) {
    case TestCardStatus.UPCOMING_TEST:
      return getDescriptionForUpcomingTest(intl, testCard);
    case TestCardStatus.TEST_ORDERED:
      return getDescriptionForTestOrdered(intl, testCard);
    case TestCardStatus.KIT_SHIPPED:
      return getDescriptionForKitShipped(intl, testCard);
    case TestCardStatus.APPT_REQUESTED:
      return intl.formatMessage(messages.testCardDescApptRequested);
    case TestCardStatus.APPT_CONFIRMED:
      return getDescriptionForAppointmentConfirmed(intl, testCard);
    case TestCardStatus.SAMPLE_RECEIVED:
      return getDescriptionForSampleReceived(intl, testCard);
    case TestCardStatus.SAMPLES_RECEIVED:
      return getDescriptionForSamplesReceived(intl, testCard);
    case TestCardStatus.PROCESSING:
      return getDescriptionForPatientResultReleaseDelayed(
        intl,
        testCard.patientResultAvailableAt!
      );
    case TestCardStatus.RESULT_READY:
      return getDescriptionForResultReady(intl, testCard);
    case TestCardStatus.TEST_COMPLETE:
      return getDescriptionForTestComplete(intl, testCard);
    case TestCardStatus.CANCELLED_TEST:
      return intl.formatMessage(messages.testCardDescCancelled);
    case TestCardStatus.EXPIRED_TEST:
      return intl.formatMessage(messages.testCardDescExpired);
    case TestCardStatus.REDRAW_NEEDED:
      return intl.formatMessage(messages.testCardDescRedrawNeeded);
    case TestCardStatus.NEW_TISSUE_NEEDED:
      return intl.formatMessage(messages.testCardDescAdditionalTissueNeeded);
  }
};

const getDescriptionForUpcomingTest = (
  intl: IntlShape,
  testCard: TestCard
): string => {
  if (isOncologyPortal(testCard.testType)) {
    return getDescriptionForOncologyUpcomingTest(intl, testCard);
  }
  if (testCard.managedBy === ManagedBy.NATERA) {
    if (testCard.isProReach) {
      return intl.formatMessage(messages.testProsperaCardDescOrderedNatera);
    }
    if (hasProsperaEbddPassed(testCard)) {
      return intl.formatMessage(messages.testCardDescProsperaEbddPassed);
    }
    const ebdd = getProsperaEbdd(testCard);
    if (ebdd) {
      return intl.formatMessage(messages.testCardProsperaScheduleAroundEbdd, {
        resultExpectedByDate: convertSampleCollectionDate(ebdd.toISOString()),
      });
    }
    return intl.formatMessage(messages.testCardBloodDrawTestOrdered);
  }
  return intl.formatMessage(messages.testCardDescOrderedClinic);
};

const getDescriptionForTestOrdered = (
  intl: IntlShape,
  testCard: TestCard
): string | undefined => {
  if (isConnectPortal(testCard.testType)) {
    return getDescriptionForConnectTestOrdered(intl, testCard);
  }
  if (isOncologyPortal(testCard.testType)) {
    return getDescriptionForOncologyTestOrdered(intl, testCard);
  }
};

const getDescriptionForKitShipped = (
  intl: IntlShape,
  testCard: TestCard
): string | undefined => {
  if (testCard.sampleType === SampleType.BLOOD) {
    return intl.formatMessage(messages.testCardDescShipped);
  }
  if (testCard.sampleType === SampleType.SALIVA) {
    return intl.formatMessage(messages.testCardDescSubmitKit);
  }
  return undefined;
};

const getDescriptionForAppointmentConfirmed = (
  intl: IntlShape,
  testCard: TestCard
): string | undefined => {
  const isSignateraSubsequentTest =
    testCard.testType === TestType.SIGNATERA && testCard.isSignateraSubsequent;
  if (isOncologyPortal(testCard.testType) && !isSignateraSubsequentTest) {
    return intl.formatMessage(messages.testCardOncologyApptConfirmedDesc);
  }
  return intl.formatMessage(messages.testCardDescApptConfirmed);
};

const getDescriptionForSampleReceived = (
  intl: IntlShape,
  testCard: TestCard
): string | undefined => {
  if (
    [
      TestType.EMPOWER,
      TestType.HORIZON,
      TestType.PANORAMA,
      TestType.VISTARA,
      TestType.SIGNATERA,
      TestType.LATITUDE,
    ].includes(testCard.testType) &&
    testCard.sampleReceivedDate
  ) {
    const estimatedResultsDeliveryDate = Utils.getUSLocaleDate(
      getEstimatedResultsDeliveryDate(
        testCard,
        new Date(testCard.sampleReceivedDate),
        testCard.isSignateraSubsequent
      )
    );
    return intl.formatMessage(messages.testCardDescSampleReceived, {
      estimatedResultsDate: estimatedResultsDeliveryDate,
    });
  } else if (testCard.testType === TestType.EMPOWER) {
    return intl.formatMessage(
      messages.testCardDefaultDescSampleReceivedEmpower
    );
  } else if (testCard.testType === TestType.HORIZON) {
    return intl.formatMessage(
      messages.testCardDefaultDescSampleReceivedHorizon
    );
  } else if (testCard.testType === TestType.PANORAMA) {
    return intl.formatMessage(
      messages.testCardDefaultDescSampleReceivedPanorama
    );
  }
  return intl.formatMessage(messages.testCardDescSample);
};

const getDescriptionForSamplesReceived = (
  intl: IntlShape,
  testCard: TestCard
): string | undefined => {
  if (testCard.sampleReceivedDate) {
    const estimatedResultsDeliveryDate = Utils.getUSLocaleDate(
      getEstimatedResultsDeliveryDate(
        testCard,
        new Date(testCard.sampleReceivedDate)
      )
    );
    if (testCard.testType === TestType.ALTERA) {
      return intl.formatMessage(messages.testCardDescSampleReceived, {
        estimatedResultsDate: estimatedResultsDeliveryDate,
      });
    }
    if (testCard.testType === TestType.SIGNATERA) {
      const estimatedResultsDeliveryDateSubsequentDate = Utils.getUSLocaleDate(
        getEstimatedResultsDeliveryDate(
          testCard,
          new Date(testCard.sampleReceivedDate),
          true
        )
      );
      return intl.formatMessage(messages.testCardDescSampleReceived, {
        estimatedResultsDate: testCard.isSignateraSubsequent
          ? estimatedResultsDeliveryDateSubsequentDate
          : estimatedResultsDeliveryDate,
      });
    }
  }
};

const getDescriptionForPatientResultReleaseDelayed = (
  intl: IntlShape,
  patientResultAvailableAt: Date
): string => {
  const resultsDelayedUntilDate = Utils.getUSLocaleDate(
    new Date(patientResultAvailableAt)
  );
  return intl.formatMessage(messages.testCardDescPatientResultReleaseDelayed, {
    resultsDelayedUntilDate,
  });
};

const getDescriptionForConnectTestOrdered = (
  intl: IntlShape,
  testCard: TestCard
): string => {
  if (
    testCard.simpleOrderWorkflowState &&
    isHorizonOrPanorama(testCard.testType) &&
    testCard.managedBy === ManagedBy.CLINIC &&
    ((testCard.simpleOrderWorkflowState ===
      SimpleOrderWorkflowState.PATIENT_ACCESSED &&
      testCard.isOrderInSignedOrdersTable) ||
      [
        SimpleOrderWorkflowState.PATIENT_SIGNED,
        SimpleOrderWorkflowState.KIT_ORDERED,
        SimpleOrderWorkflowState.COMPLETED,
      ].includes(testCard.simpleOrderWorkflowState))
  ) {
    return intl.formatMessage(messages.testCardDescOrderedConnectPatientSigned);
  }
  if (testCard.nextStatus === TestCardStatus.SAMPLE_RECEIVED) {
    if (testCard.managedBy === ManagedBy.CLINIC) {
      return intl.formatMessage(
        messages.testCardDescOrderedConnectManagedByClinic
      );
    }
    return intl.formatMessage(messages.testCardDescOrderedConnect);
  }
  return intl.formatMessage(messages.testCardDescOrderedConnectDraft);
};

const getDescriptionForOncologyTestOrdered = (
  intl: IntlShape,
  testCard: TestCard
): string | undefined => {
  if (testCard.managedBy === ManagedBy.CLINIC) {
    return intl.formatMessage(
      messages.testCardOncologyManagedByClinicTestOrdered
    );
  }
  if (testCard.managedBy === ManagedBy.NATERA) {
    return intl.formatMessage(
      messages.testCardOncologyManagedByNateraTestOrdered
    );
  }
};

const getDescriptionForOncologyUpcomingTest = (
  intl: IntlShape,
  testCard: TestCard
): string => {
  if (testCard.testType === TestType.SIGNATERA) {
    if (testCard.managedBy === ManagedBy.CLINIC) {
      return testCard.isSignateraSubsequent
        ? intl.formatMessage(
            messages.testCardSignateraOrderedByClinicSubsequentTestDesc
          )
        : intl.formatMessage(
            messages.testCardSignateraOrderedByClinicFirstTestDesc
          );
    }
    if (
      testCard.managedBy === ManagedBy.NATERA &&
      testCard.sampleCollectionDate
    ) {
      return intl.formatMessage(
        messages.testCardOncologyManagedByNateraTestOrderedSampleCollectionDate,
        {
          sampleCollectionDate: convertSampleCollectionDate(
            testCard.sampleCollectionDate.toString()
          ),
        }
      );
    }
  }
  return intl.formatMessage(
    messages.testCardOncologyManagedByNateraTestOrdered
  );
};

const getDescriptionForResultReady = (
  intl: IntlShape,
  testCard: TestCard
): string => {
  // Special case for Empower STAT panel - show partial results message
  if (
    testCard.testType === TestType.EMPOWER &&
    testCard.isEmpowerStatPanelIncluded &&
    testCard.sampleReceivedDate &&
    testCard.isLatestReleasedResultStat
  ) {
    const estimatedResultsDeliveryDate = Utils.getUSLocaleDate(
      getEstimatedResultsDeliveryDate(
        testCard,
        new Date(testCard.sampleReceivedDate)
      )
    );

    return intl.formatMessage(
      messages.testCardDescResultReadyReviewEmpowerStat,
      { estimatedResultsDate: estimatedResultsDeliveryDate }
    );
  }

  // All test types should show the same message for RESULT_READY status
  return intl.formatMessage(messages.testCardDescResultReadyReview);
};

const getDescriptionForTestComplete = (
  intl: IntlShape,
  testCard: TestCard
): string => {
  if (testCard.viewStatus === TestStatus.VIEW_WITH_PHYSICIAN) {
    return intl.formatMessage(messages.testCardDescComplete);
  }
  return intl.formatMessage(messages.testCardDescResultReadyReview);
};
